import React, { useState } from "react";
import { Ruler, Crosshair, Target } from "@phosphor-icons/react";
import { station, fmt, formatDMS } from "../lib/api";
import { toast } from "sonner";

export const MeasurePanel = ({ disabled, onMeasured }) => {
  const [busy, setBusy] = useState(null); // "distance" | "angle" | "coord"
  const [last, setLast] = useState(null);

  const measure = async (mode) => {
    if (disabled) return;
    setBusy(mode);
    try {
      const res = await station.measure(mode);
      setLast(res);
      onMeasured?.(res);
      if (res?.error) {
        toast.error(`${mode.toUpperCase()}: ${res.error}`);
      } else {
        toast.success(`${mode.toUpperCase()} captured`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Measurement failed";
      toast.error(`${mode.toUpperCase()}: ${detail}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="panel" data-testid="measure-panel">
      <div className="panel-header">
        <span>MEASURE</span>
      </div>
      <div className="grid grid-cols-3 gap-2 p-4">
        <MeasureBtn
          testid="measure-distance-btn"
          icon={Ruler}
          label="Distance"
          loading={busy === "distance"}
          onClick={() => measure("distance")}
          disabled={disabled}
        />
        <MeasureBtn
          testid="measure-angle-btn"
          icon={Crosshair}
          label="Angle"
          loading={busy === "angle"}
          onClick={() => measure("angle")}
          disabled={disabled}
        />
        <MeasureBtn
          testid="measure-coord-btn"
          icon={Target}
          label="Coord"
          loading={busy === "coord"}
          onClick={() => measure("coord")}
          disabled={disabled}
        />
      </div>

      <div className="border-t border-[var(--border)] px-4 py-3" data-testid="last-measurement">
        <div className="mb-1 text-[10px] uppercase tracking-widest text-[var(--text-dim)]">
          Last measurement
        </div>
        {last ? (
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 readout text-sm tabular-nums">
            <span className="text-[var(--text-dim)]">Mode</span>
            <span>{last.mode.toUpperCase()}</span>
            <span className="text-[var(--text-dim)]">Hz</span>
            <span>{formatDMS(last.hz)}</span>
            <span className="text-[var(--text-dim)]">V</span>
            <span>{formatDMS(last.v)}</span>
            {last.sd !== null && last.sd !== undefined && (
              <>
                <span className="text-[var(--text-dim)]">SD</span>
                <span>{fmt(last.sd, 3)} m</span>
              </>
            )}
            {last.x !== null && last.x !== undefined && (
              <>
                <span className="text-[var(--text-dim)]">X / Y / Z</span>
                <span className="truncate">
                  {fmt(last.x, 2)} / {fmt(last.y, 2)} / {fmt(last.z, 2)}
                </span>
              </>
            )}
            {last.error && (
              <>
                <span className="text-[var(--danger)]">Error</span>
                <span
                  className="truncate text-[var(--danger)]"
                  data-testid="measure-error"
                  title={last.error}
                >
                  {last.error}
                </span>
              </>
            )}
          </div>
        ) : (
          <div className="text-sm text-[var(--text-mute)]">—</div>
        )}
      </div>
    </div>
  );
};

const MeasureBtn = ({ testid, icon: Icon, label, onClick, disabled, loading }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    disabled={disabled || loading}
    className="btn-tac flex flex-col items-center justify-center gap-1"
    data-variant={loading ? "amber" : undefined}
  >
    <Icon size={24} weight="bold" />
    <span className="text-xs">{loading ? "…" : label}</span>
  </button>
);
